/**
 * Broadcasts tab on the SMS page — create a bulk send to a list, watch
 * live fan-out/delivery progress, cancel while queued or sending.
 *
 * Progress numbers come from the live sms_messages aggregate the API
 * returns per broadcast (live_*), so the meter keeps moving while the
 * send worker drains the queue — even after the broadcast row itself is
 * 'completed'.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  smsCampaigns, type SmsBroadcastRow, type BroadcastStatus,
} from '@/api/smsCampaigns';
import { dids } from '@/api/dids';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { SmsListPicker } from '@/components/SmsListPicker';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateTimePicker } from '@/components/DateTimePicker';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Radio, Plus, Ban, FolderOpen, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/cn';

const POLL_MS = 10000;

export function BroadcastsTab({ canMutate }: { canMutate: boolean }) {
  const customerId = useEffectiveCustomerId();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sms-broadcasts', customerId],
    queryFn: () => smsCampaigns.listBroadcasts({ customer_id: customerId ?? undefined }),
    refetchInterval: POLL_MS,
  });
  const rows = data?.rows ?? [];

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground max-w-md">
            Each recipient is checked against the Do-Not-Contact list before
            sending. Big lists drain gradually — around 100 texts per minute.
          </p>
          {canMutate && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 me-1.5" />
              New broadcast
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading broadcasts…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <Radio className="mx-auto h-7 w-7 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">No broadcasts yet</p>
            <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
              Send one message to every subscriber on a list — now, or on a
              schedule.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {rows.map((b) => (
              <BroadcastRow key={b.id} row={b} canMutate={canMutate} />
            ))}
          </ul>
        )}
      </CardContent>
      {canMutate && <CreateBroadcastDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </Card>
  );
}

const STATUS_META: Record<BroadcastStatus, { label: string; cls: string }> = {
  draft:     { label: 'draft',     cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  scheduled: { label: 'queued',    cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  running:   { label: 'sending',   cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
  paused:    { label: 'paused',    cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  completed: { label: 'completed', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  cancelled: { label: 'cancelled', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
};

function BroadcastRow({ row, canMutate }: { row: SmsBroadcastRow; canMutate: boolean }) {
  const qc = useQueryClient();
  const meta = STATUS_META[row.status] ?? STATUS_META.draft;
  const cancellable = row.status === 'scheduled' || row.status === 'running';

  const cancel = useMutation({
    mutationFn: () => smsCampaigns.cancelBroadcast(row.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-broadcasts'] }),
  });

  const total = row.live_total ?? 0;
  const sent = row.live_sent ?? 0;
  const failed = row.live_failed ?? 0;
  const blocked = row.live_blocked_dnc ?? 0;
  const queued = row.live_queued ?? 0;

  return (
    <li className="rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{row.name}</span>
            <span className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1',
              meta.cls,
            )}>
              {meta.label}
            </span>
            {row.source === 'folder_notify' && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                title="Created automatically when a recording was added to a folder"
              >
                <FolderOpen className="h-3 w-3" />
                Folder alert
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{row.body}</p>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
            {row.list_name && <span>to <span className="font-medium">{row.list_name}</span></span>}
            {row.scheduled_at && row.status === 'scheduled' && (
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-3 w-3" />
                {new Date(row.scheduled_at).toLocaleString(undefined, {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                })}
              </span>
            )}
            <time dateTime={row.created_at}>
              created {new Date(row.created_at).toLocaleDateString()}
            </time>
          </div>
        </div>
        {canMutate && cancellable && (
          <Button
            size="sm" variant="outline"
            className="shrink-0 border-rose-200 text-rose-600 hover:bg-rose-50"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            <Ban className="h-3.5 w-3.5 me-1.5" />
            Cancel
          </Button>
        )}
      </div>

      {/* Segmented live progress meter: sent / queued / blocked / failed. */}
      {total > 0 && (
        <div className="mt-3">
          <div
            className="flex h-1.5 w-full overflow-hidden rounded-full bg-secondary"
            role="img"
            aria-label={`${sent} sent, ${queued} queued, ${blocked} blocked, ${failed} failed of ${total}`}
          >
            <div className="bg-emerald-500" style={{ width: `${(sent / total) * 100}%` }} />
            <div className="bg-amber-400" style={{ width: `${(queued / total) * 100}%` }} />
            <div className="bg-slate-400" style={{ width: `${(blocked / total) * 100}%` }} />
            <div className="bg-rose-500" style={{ width: `${(failed / total) * 100}%` }} />
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground tabular-nums flex-wrap">
            <span><span className="font-semibold text-emerald-700">{sent}</span> sent</span>
            {queued > 0 && <span><span className="font-semibold text-amber-700">{queued}</span> queued</span>}
            {blocked > 0 && <span><span className="font-semibold text-slate-600">{blocked}</span> DNC-blocked</span>}
            {failed > 0 && <span><span className="font-semibold text-rose-700">{failed}</span> failed</span>}
            <span className="ms-auto">{total} total</span>
          </div>
        </div>
      )}
    </li>
  );
}

function CreateBroadcastDialog({
  open, onOpenChange,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const customerId = useEffectiveCustomerId();
  const [listId, setListId] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [from, setFrom] = useState('');
  const [schedule, setSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  // From-number: the customer's own active numbers, like the compose dialog.
  const didsQuery = useQuery({
    queryKey: ['dids', 'sms-broadcast-from', customerId],
    queryFn: () => dids.list({ customerId: customerId!, limit: 200 }),
    enabled: open && Boolean(customerId),
    staleTime: 60_000,
  });
  const fromOptions = useMemo(
    () => (didsQuery.data?.rows ?? []).filter((d) => d.active),
    [didsQuery.data?.rows],
  );

  const create = useMutation({
    mutationFn: () => smsCampaigns.createBroadcast({
      list_id: listId!,
      body: body.trim(),
      from_number: from || null,
      scheduled_at: schedule && scheduledAt
        ? new Date(scheduledAt).toISOString()
        : null,
      ...(customerId ? { customer_id: customerId } : {}),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sms-broadcasts'] });
      setListId(null); setBody(''); setFrom(''); setSchedule(false); setScheduledAt('');
      setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  const chars = body.length;
  const parts = chars === 0 ? 0 : Math.ceil(chars / 160);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New broadcast</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <SmsListPicker
            value={listId}
            onChange={setListId}
            label="Send to list"
            allowNone={false}
          />
          <div className="space-y-1">
            <Label htmlFor="bc-from">From number (optional)</Label>
            {fromOptions.length > 0 ? (
              <Select value={from || undefined} onValueChange={setFrom}>
                <SelectTrigger id="bc-from" className="font-mono">
                  <SelectValue placeholder="Account default number" />
                </SelectTrigger>
                <SelectContent>
                  {fromOptions.map((d) => (
                    <SelectItem key={d.id} value={d.e164} className="font-mono">
                      {d.e164}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="bc-from" className="font-mono"
                value={from} onChange={(e) => setFrom(e.target.value)}
                placeholder="Account default number"
              />
            )}
            <p className="text-xs text-muted-foreground">
              Leave blank to send from this account&rsquo;s first active number.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="bc-body">Message</Label>
            <Textarea
              id="bc-body" rows={4}
              value={body} onChange={(e) => setBody(e.target.value)}
              maxLength={1600}
              placeholder="Type the message every subscriber receives…"
            />
            <div className="flex items-center justify-end text-xs">
              <span className={cn(
                'font-mono tabular-nums',
                chars > 160 ? 'text-amber-600' : 'text-muted-foreground',
              )}>
                {chars}/1600{parts > 1 ? ` • ${parts} parts` : ''}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={schedule}
                onChange={(e) => setSchedule(e.target.checked)}
              />
              Schedule for later
            </label>
            {schedule && (
              /* In-house calendar (same widget as Time Frames) — never the
                 browser-native datetime-local. */
              <DateTimePicker
                value={scheduledAt}
                onChange={setScheduledAt}
                placeholder="Pick a send time"
                className="w-full justify-start"
              />
            )}
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            disabled={create.isPending || !listId || !body.trim() || (schedule && !scheduledAt)}
            onClick={() => create.mutate()}
          >
            <Radio className="h-3.5 w-3.5 me-1.5" />
            {create.isPending
              ? 'Queuing…'
              : schedule ? 'Schedule broadcast' : 'Send broadcast'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
